import { exportPublicKey, generateKeyPair, generateUuid, signMessage } from "../lib/crypto";
import { getIdentity, getPrivateKey, setIdentity, setPrivateKey } from "./storage";

function getApiBase(): string {
	if (location.hostname === "localhost" || location.hostname === "127.0.0.1") {
		return "http://localhost:8787";
	}
	return "https://wayf-backend.ketilovre.workers.dev";
}

export class AuthError extends Error {
	constructor(
		message: string,
		public readonly code: "NETWORK_ERROR" | "INVALID_RESPONSE" | "USER_EXISTS" | "INVALID_CREDENTIALS"
	) {
		super(message);
		this.name = "AuthError";
	}
}

export interface RegisterResult {
	uuid: string;
	nickname: string;
}

export async function register(nickname: string): Promise<RegisterResult> {
	const uuid = generateUuid();
	const keyPair = await generateKeyPair();
	const publicKeyJwk = await exportPublicKey(keyPair.publicKey);

	let response: Response;
	try {
		response = await fetch(`${getApiBase()}/auth/register`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ uuid, nickname, publicKey: publicKeyJwk }),
			credentials: "include"
		});
	} catch {
		throw new AuthError("Network error during registration", "NETWORK_ERROR");
	}

	if (response.status === 409) {
		throw new AuthError("User already exists", "USER_EXISTS");
	}

	if (!response.ok) {
		const text = await response.text();
		throw new AuthError(text || "Registration failed", "INVALID_RESPONSE");
	}

	await setIdentity({ uuid, nickname });
	await setPrivateKey(keyPair.privateKey);

	return { uuid, nickname };
}

export interface LoginResult {
	nickname: string;
}

export async function login(): Promise<LoginResult> {
	const identity = await getIdentity();
	const privateKey = await getPrivateKey();

	if (!identity || !privateKey) {
		throw new AuthError("No stored credentials", "INVALID_CREDENTIALS");
	}

	const timestamp = Date.now();
	const signature = await signMessage(timestamp.toString(), privateKey);

	let response: Response;
	try {
		response = await fetch(`${getApiBase()}/auth/login`, {
			method: "POST",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ uuid: identity.uuid, timestamp, signature }),
			credentials: "include"
		});
	} catch {
		throw new AuthError("Network error during login", "NETWORK_ERROR");
	}

	if (response.status === 401) {
		throw new AuthError("Invalid credentials", "INVALID_CREDENTIALS");
	}

	if (!response.ok) {
		const text = await response.text();
		throw new AuthError(text || "Login failed", "INVALID_RESPONSE");
	}

	const data = await response.json();
	return { nickname: data.nickname };
}

export async function logout(): Promise<void> {
	try {
		await fetch(`${getApiBase()}/auth/logout`, {
			method: "POST",
			credentials: "include"
		});
	} catch {
		// Ignore network errors on logout
	}
}

export async function isAuthenticated(): Promise<boolean> {
	const identity = await getIdentity();
	const privateKey = await getPrivateKey();
	return identity !== null && privateKey !== null;
}

export async function getCurrentUser(): Promise<{ uuid: string; nickname: string } | null> {
	return await getIdentity();
}
